/**
 * @description Component-AccountHistory
 */
import './index.less';

import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import Empty from '@/components/Empty';
import { ExternalLink } from '@/components/Link';
import TokenSymbol from '@/components/TokenLogo/TokenSymbol';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { useInfiniteAccountBalanceHistory } from '@/features/graph/hooks';
import { AccountBalanceHistory as AccountBalanceHistoryType } from '@/features/graph/types';
import { useChainId, useEtherscanLink, useUserAddr } from '@/hooks/web3/useChain';
import { formatDate } from '@/utils/timeUtils';
import { mappingToken } from '@/utils/token';
import MobileHistoryCard from '../MobileHistoryCard';
import MobileHistorySkeleton from '../MobileHistoryCard/Skeleton';

interface IPropTypes {
  isTradePage?: boolean;
}

const AccountHistory: FC<IPropTypes> = () => {
  const chainId = useChainId();
  const userAddr = useUserAddr();
  const { t } = useTranslation();

  const {
    histories,
    queryState: { isLoading: isLoadingAccount, isFetching },
  } = useInfiniteAccountBalanceHistory({ chainId, userAddr });

  const getEtherscanLink = useEtherscanLink();
  const loading = isLoadingAccount || isFetching;

  const renderCard = (record: AccountBalanceHistoryType) => {
    return (
      <MobileHistoryCard key={record.id}>
        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.time')}</div>
          <div className="value">
            <ExternalLink href={getEtherscanLink(record.txHash, 'transaction')}>
              <div className="history-time-with-icon">
                <span>{formatDate(record.timestamp * 1000)}</span>
              </div>
            </ExternalLink>
          </div>
        </div>
        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.account')}</div>
          <div className="value">
            {chainId && (
              <TokenSymbol
                classNames="history-token-symbol"
                tokenInfo={mappingToken({
                  token: record.tokenInfo,
                  chainId: chainId,
                })}></TokenSymbol>
            )}
          </div>
        </div>
        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.type')}</div>
          <div className="value">{record.typeString === 'deposit' ? t('common.deposit') : t('common.withdraw')}</div>
        </div>
        <div className="syn-mobile-history-card-row">
          <div className="label">{t('common.table.amount')}</div>
          <div className="value">
            {WrappedBigNumber.from(record.value).formatNumberWithTooltip({
              suffix: record?.tokenInfo?.symbol,
            })}
          </div>
        </div>
      </MobileHistoryCard>
    );
  };

  return (
    <>
      <div className="syn-mobile-history-account">
        {loading ? <MobileHistorySkeleton /> : histories.slice(0, 10).map(renderCard)}
      </div>
      {!loading && !!histories.length && <div className="syn-mobile-history-info">{t('mobile.history.tableInfo')}</div>}
      {!loading && !histories?.length && <Empty type="vertical" />}
    </>
  );
};

export default AccountHistory;
